import { Component, OnInit } from '@angular/core';
import { HelpersService } from '@app/shared/services/helpers.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { AdminScopes, AdminUser, UserService } from '@app/shared/services/user.service';
import { Router } from '@angular/router';
import { first } from 'rxjs';
import { Animations, DateFormats, DefaultTimezone, ShippingProviders, SupportedAirports } from '@app/shared/constants';
import { environment } from '../../../../../environments/environment';
import { AwbShipmentRO, ReturnedDocumentDto, ShipmentsService } from '@app/shared/services/shipments.service';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { DocumentTypes } from '@app/pages/authenticated/components/shipment-details/shipment-details.component';
import { ViewDocumentComponent } from '@app/pages/authenticated/components/view-document/view-document.component';
import { MtxDrawer } from '@ng-matero/extensions/drawer';
import * as FileSaver from 'file-saver';
import { DateTime } from 'luxon';


@Component({
  standalone: false,
  selector: 'app-awb-labels',
  templateUrl: './awb-labels.component.html',
  animations: [Animations.ExpandAnimation],
})
export class AwbLabelsComponent implements OnInit {
  public loading: boolean = true;
  public user: AdminUser;
  public canEditAwb: boolean = false;
  public supportedAirports = SupportedAirports;
  public selectedAirport: string;
  public loadingAWBs: boolean = false;
  public openingModal: boolean = false;
  public downloadingAllAWBLabels: boolean = false;
  public groupedAWBs: { sortDate: string,shipDate: string, awbs: AwbShipmentRO[] }[];
  public expandedElement = null;
  public DATE_FORMAT: string = DateFormats.DATE_SHORT_MONTH;
  public CDN_URL: string = environment.CDN_URL;
  public displayedColumns: string[] = ['select', 'sortDate', 'date'];
  public readonly DefaultTimezone = DefaultTimezone;

  constructor(
    private helpersService: HelpersService,
    private logger: LoggerService,
    private userService: UserService,
    private shipmentsService: ShipmentsService,
    private bannerNotificationsService: BannerNotificationsService,
    private router: Router,
    private drawer: MtxDrawer,
  ) {}
  ngOnInit() {
    this.userService
      .getAuthenticatedUser([AdminScopes.VIEW_AWB_LABELS])
      .pipe(first())
      .subscribe(
        (adminUser: AdminUser) => {
          if (adminUser) {
            this.user = adminUser;
            this.canEditAwb = this.userService.hasAuthScope(
              this.user,
              [AdminScopes.MODIFY_AWB_LABELS]
            );
            const userScopes = this.user.scopes;
            const hasLimit = userScopes.filter((scope: AdminScopes) => scope.startsWith(AdminScopes.VIEW_AWB_LABELS + '.'));
            if (hasLimit.length > 0) {
              const airportCodes = hasLimit.map((scope: AdminScopes) => scope.split('.')[2].toUpperCase().trim());
              this.supportedAirports = SupportedAirports.filter((airport: any) => airportCodes.includes(airport.code));
            }
            this.loading = false;
          } else {
            this.router.navigate(['login']);
          }
        },
        (err) => {
          this.router.navigate(['home']);
        }
      );
  }

  public getAWBs(): void {
    if (!this.loadingAWBs) {
      this.loadingAWBs = true;
      this.groupedAWBs = [];
      this.logger.log('GET AWBs', { airport: this.selectedAirport });
      this.shipmentsService.getAWBs({ originAirport: this.selectedAirport }).subscribe(
        (awbs: AwbShipmentRO[]) => {
          this.logger.log('GET AWBs success', awbs);
          awbs.forEach(awb => {
            awb.lastMileProviderName = this.helpersService.convertShipmentProviderToString(awb.lastMileProvider);
            const sortDate = DateTime.fromISO(awb.shipDate).minus({ days: 1 }).toFormat(this.DATE_FORMAT);
            if (!this.groupedAWBs.find(group => group.sortDate === sortDate)) {
              this.groupedAWBs.push({ sortDate, shipDate: awb.shipDate, awbs: [awb] });
            } else {
              this.groupedAWBs.find(group => group.sortDate === sortDate).awbs.push(awb);
            }
          });
          this.groupedAWBs.sort((a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime());
          this.loadingAWBs = false;
        },
        (err) => {
          this.bannerNotificationsService.error('Could not get list of AWBs', {
            category: 'GET AWBs error',
            message: err,
          });
          this.loadingAWBs = false;
        },
      );
    }
  }

  public downloadAllAWBLabelsForDate(date: string, showPreview: boolean = false): void {
    if (!this.downloadingAllAWBLabels) {
      this.downloadingAllAWBLabels = true;
      const allAWBsForDate = this.groupedAWBs.find(group => group.shipDate === date).awbs;
      if ((allAWBsForDate || []).length > 0) {
        const getAllAWBLabelsDto = {
          type: DocumentTypes.LABELS,
          documents: allAWBsForDate.map((awb) => {
            return {
              provider: ShippingProviders.FREIGHT_AIR_CANADA,
              id: awb.awbNumber,
              uuid: awb.uuid,
              extension: 'pdf'
            }}),
          extension: 'pdf'
        };

        this.shipmentsService.getAllAWBLabels(getAllAWBLabelsDto).subscribe(
          (res: ReturnedDocumentDto) => {
            this.logger.log('GET all AWB labels success', res);
            const mimeType = this.shipmentsService.getMimeType(res.extension);
            const downloadFileName = `ALL-AWB-LABELS-${allAWBsForDate[0].originAirport}-${date}.${res.extension}`;

            if (showPreview) {
              if (!this.openingModal) {
                this.openingModal = true;
                this.drawer.open(ViewDocumentComponent, {
                  width: this.helpersService.getDrawerWidth(),
                  disableClose: true,
                  closeOnNavigation: false,
                  data: {
                    document: res.fileData,
                    downloadFileName,
                    previewTitle: 'View Invoice',
                  },
                });
                this.openingModal = false;
              }
            } else {
              const blob = this.helpersService.b64toBlob(res.fileData, mimeType);
              FileSaver.saveAs(blob, downloadFileName);
            }

            this.downloadingAllAWBLabels = false;
          },
          (err) => {
            this.bannerNotificationsService.error('Could not get all AWB labels', {
              category: 'GET all AWB labels error',
              message: err,
            });
            this.downloadingAllAWBLabels = false;
          },
        );
      }
    }
  }

  public toggleAWBDetails(awbRow: any) {
    const sameRowClicked = this.expandedElement === awbRow;
    this.expandedElement = sameRowClicked ? null : awbRow;
  }
}
